import PoweredBy from "../../../components/PoweredBy";
import { linksConstants } from "../../../constants";
import useAppAccessor from "../../../hooks/useappAccessor";
import { getBrandColor } from "../../../utils/brand.util";

interface WaitingRoomProps {
  hostName?: string;
  companyName?: string;
  referrerName?: string;
  hostPicture?: string;
}

export default function WaitingRoom({
  hostName,
  companyName,
  referrerName,
  hostPicture,
}: WaitingRoomProps) {
  const { getUserData } = useAppAccessor();
  const { language, client } = getUserData();

  const isMobile =
    window.innerWidth < 768 ||
    /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent);

  const brandColor = getBrandColor(client);

  function handleLinkClick(event: any, url: any) {
    event.preventDefault();
    window.open(url, "_blank");
  }

  const getInitials = (name: string) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div 
      className="flex flex-1 items-center justify-center min-h-screen"
      style={{
        background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 50%, #1a1a1a 100%)",
      }}
    >
      <div className="flex flex-col items-center w-full px-4 py-8 max-w-2xl">
        <div
          className="flex flex-col items-center w-full rounded-3xl overflow-hidden relative"
          style={{
            background: "linear-gradient(145deg, #2d2d2d 0%, #1a1a1a 100%)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5), 0 4px 16px rgba(0, 0, 0, 0.3)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            maxWidth: isMobile ? "100%" : "520px",
          }}
        >
          <div 
            className="absolute top-0 left-0 right-0 h-1 rounded-t-3xl"
            style={{ backgroundColor: brandColor }}
          />
          
          <div className="flex flex-col items-center px-8 py-10 w-full">
            <div
              className="flex items-center justify-center rounded-full mb-6 transition-transform duration-300 hover:scale-105"
              style={{
                width: "96px",
                height: "96px",
                background: hostPicture 
                  ? "transparent"
                  : `linear-gradient(135deg, ${brandColor} 0%, ${brandColor}dd 100%)`,
                boxShadow: `0 4px 16px ${brandColor}40, 0 2px 8px ${brandColor}20`,
              }}
            >
              {hostPicture ? (
                <img
                  className="h-full w-full object-cover rounded-full"
                  src={hostPicture}
                  alt="Host"
                />
              ) : (
                <span 
                  className="text-white font-semibold text-3xl"
                  style={{ textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)" }}
                >
                  {getInitials(hostName || "")}
                </span>
              )}
            </div>

            <div className="flex flex-col items-center w-full space-y-4 mb-8">
              <p className="text-white text-base font-medium text-center leading-relaxed">
                {language['livestream_hosted']}{" "}
                <span
                  className="font-semibold"
                  style={{ color: brandColor }}
                >
                  {hostName}
                </span>
                {" "}{language['starting_soon_text']}
              </p>

              <div className="flex flex-col items-center w-full space-y-3 pt-2">
                {companyName && (
                  <div className="flex items-center justify-center">
                    <span className="text-gray-400 text-sm mr-2">
                      {language['representing_label']}
                    </span>
                    <span 
                      className="text-white text-sm font-medium"
                      style={{ color: brandColor }}
                    >
                      {companyName}
                    </span>
                  </div>
                )}

                {referrerName && (
                  <div className="flex items-center justify-center">
                    <span className="text-gray-400 text-sm mr-2">
                      {language['referred_by_label']}
                    </span>
                    <span 
                      className="text-white text-sm font-medium"
                      style={{ color: brandColor }}
                    >
                      {referrerName}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div 
              className="w-full pt-6 border-t"
              style={{ borderColor: "rgba(255, 255, 255, 0.1)" }}
            >
              <p className="text-gray-400 text-xs text-center leading-relaxed">
                {language["join_agreement"]}{" "}
                <a
                  href={linksConstants.data_url}
                  className="transition-colors duration-200 hover:opacity-80"
                  style={{ color: brandColor }}
                  onClick={(e) => handleLinkClick(e, linksConstants.data_url)}
                >
                  {language["data_privacy_agreement"].charAt(0).toUpperCase() +
                    language["data_privacy_agreement"].slice(1)}
                </a>
                ,{" "}
                <a
                  href={linksConstants.privacy_url}
                  className="transition-colors duration-200 hover:opacity-80"
                  style={{ color: brandColor }}
                  onClick={(e) => handleLinkClick(e, linksConstants.privacy_url)}
                >
                  {language["privacy_policy"].charAt(0).toUpperCase() +
                    language["privacy_policy"].slice(1)}
                </a>{" "}
                and{" "}
                <a
                  href={linksConstants.terms_url}
                  className="transition-colors duration-200 hover:opacity-80"
                  style={{ color: brandColor }}
                  onClick={(e) => handleLinkClick(e, linksConstants.terms_url)}
                >
                  {language["terms_of_use"].charAt(0).toUpperCase() +
                    language["terms_of_use"].slice(1)}
                </a>
                .
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <PoweredBy />
        </div>
      </div>
    </div>
  );
}
